import {
	CampaignObjective,
	CreateCreativeType,
	CreateGroupBidType,
} from 'wb-reddit/graphql/generated';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import { SuperGridMutation, SuperGridValidationEntity } from '@aleph/supergrid';
import {
	generateMutationHash, SpotifyCreateData, SpotifyCreateLevels,
	SpotifyGroupCreateData, SpotifySuperGridMutationsConfig,
} from 'wb-spotify/app/routes/superGrid/mutator/utils';
import {
	AdStatus, CampaignStatus, CreateAdInput,
	CreateCampaignInput, CreateGroupInput, GroupPacing,
	GroupStatus,
	GroupTargetsGender,
	GroupTargetsPlacement,
	GroupTargetsPlatform,
	GroupTargetsPodcastEpisodeTopic,
	GroupTargetsSensitiveTopicExclusionsFilterOption,
} from 'wb-spotify/graphql/generated';
import { omit } from 'lodash';

export function prepareSupergridGroupTargetsForBackend(group: SpotifyGroupCreateData) {
	return {
		groupTargetsArtistIds:
			group.groupTargets?.groupTargetsArtists
				?.map((artist) => artist.value as string),
		groupTargetsGeoTargets: (group.groupTargets?.groupTargetsGeoTargets?.countryCode?.value
			|| group.groupTargets?.groupTargetsGeoTargets?.cityIds?.length
			|| group.groupTargets?.groupTargetsGeoTargets?.dmaIds?.length
			|| group.groupTargets?.groupTargetsGeoTargets?.regionIds?.length
			|| group.groupTargets?.groupTargetsGeoTargets?.postalCodeIds?.length)
			? (
				{
					// @ts-ignore TODO fix
					countryCode: group.groupTargets?.groupTargetsGeoTargets?.countryCode?.value as string,
					cityIds:     group.groupTargets?.groupTargetsGeoTargets?.cityIds
						?.map((city) => (city?.value ? city.value : city)),
					dmaIds: group.groupTargets?.groupTargetsGeoTargets?.dmaIds
						?.map((dma) => (dma?.value ? dma.value : dma)),
					regionIds: group.groupTargets?.groupTargetsGeoTargets?.regionIds
						?.map((region) => (region?.value ? region.value : region)),
					postalCodeIds: group.groupTargets?.groupTargetsGeoTargets?.postalCodeIds
						?.map((postalCode) => (postalCode?.value ? postalCode.value : postalCode)),
				}
			) : undefined,
		groupTargetsPlacements: group.groupTargets?.groupTargetsPlacements
			?.map((placement) => placement.value as GroupTargetsPlacement) ?? [],
		groupTargetsPodcastEpisodeTopicIds:
			group.groupTargets?.groupTargetsPodcastEpisodeTopics
				?.map((topic) => topic.value as GroupTargetsPodcastEpisodeTopic),
		groupTargetsAgeRanges: group.groupTargets?.groupTargetsAgeRanges?.map((ageRange) => ({
			min: Number(ageRange.value.split('-')[0]),
			max: Number(ageRange.value.split('-')[1]),
		})),
		groupTargetsGenders: group.groupTargets?.groupTargetsGenders
			? [group.groupTargets?.groupTargetsGenders as GroupTargetsGender]
			: undefined,
		groupTargetsInterestIds:
			group.groupTargets?.groupTargetsInterests?.map(option => option.value as string),
		groupTargetsLanguage:  group.groupTargets?.groupTargetsLanguage?.language?.value as string,
		groupTargetsPlatforms: group.groupTargets?.groupTargetsPlatforms
			?.map((platform) => platform.value as GroupTargetsPlatform),
		groupTargetsSensitiveTopicExclusions: {
			topics: group.groupTargets?.groupTargetsSensitiveTopicExclusions?.sensitiveTopics
				?.map((topic) => ({
					id:           topic.value as string,
					filterOption: GroupTargetsSensitiveTopicExclusionsFilterOption.Standard,
				})),
		},
		groupTargetsGenreIds:    group.groupTargets?.groupTargetsGenres?.map((genre) => genre.value as string),
		groupTargetsPlaylistIds: group.groupTargets?.groupTargetsPlaylists?.map((playlist) => playlist.value as string),
	};
}

export const getGroupBidTypeForObjective = (objective: CampaignObjective): CreateGroupBidType => {
	switch (objective) {
		case CampaignObjective.Impressions: // BRAND AWARENESS AND REACH
			return CreateGroupBidType.Cpm;
		case CampaignObjective.VideoViewableImpressions: // VIEW VIEWS
			return CreateGroupBidType.Cpv;
	}

	return CreateGroupBidType.Cpc;
};

export const preValidateCreatives = (creatives: SpotifyCreateData[]): SuperGridValidationEntity[] => {
	const errors: SuperGridValidationEntity[] = [];

	creatives.forEach(creative => {
		if (creative.creativeType === CreateCreativeType.Video && !creative.creativeThumbnailUrl) {
			errors.push({
				id:      creative.hash,
				level:   SpotifyLevel.AD,
				field:   'creativeThumbnailUrl',
				message: 'Thumbnail is required for video creatives',
			});
		}
	});

	return errors;
};

// TODO: add ad specific validations & append creative validations
export const preValidateAds = (ads: SpotifyCreateData[]): SuperGridValidationEntity[] => [
	...preValidateCreatives(ads),
];

export type SpotifyMutationCampaign = SuperGridMutation<SpotifySuperGridMutationsConfig, SpotifyCreateLevels.CAMPAIGN>;

export function getSpotifyCampaignVariablesFromSupergridMutation(campaignData: SpotifyMutationCampaign, status: CampaignStatus): CreateCampaignInput {
	const result = {
		campaignHash:      (campaignData.hash ?? '').toString(),
		campaignName:      campaignData.name ?? '',
		campaignObjective: campaignData.campaignObjective,
		campaignStatus:    status,
		adAccountId:       campaignData.parent?.adAccountId,
	};

	return result;
}

export function getSpotifyGroupVariablesFromSupergridMutation(group: SpotifyGroupCreateData, status: GroupStatus): CreateGroupInput {
	const result = {
		// adAccountId:         group.parent?.parent?.adAccountId,
		campaignId:          group.parent?.id,
		categoryId:          group.category.value as string,
		groupAssetFormat:    group.groupAssetFormat,
		groupBidMicroAmount: group.groupBidMicroAmount,
		groupBidStrategy:    group.groupBidStrategy,
		groupBudget:         {
			type:        group.groupBudget?.type,
			microAmount: group.groupBudget?.microAmount,
		},
		groupTargets:       prepareSupergridGroupTargetsForBackend(group),
		groupHash:          group.hash ?? generateMutationHash(),
		groupName:          group.name,
		groupStartTime:     group.groupStartTime,
		groupEndTime:       group.groupEndTime,
		groupStatus:        status,
		// Remove __typename from groupFrequencyCaps
		groupFrequencyCaps: group.groupFrequencyCaps?.map(cap => omit(cap, '__typename')),
		groupPacing:        group.groupPacing as GroupPacing,
	};

	// @ts-ignore TODO fix type
	return result;
}

export function getSpotifyAdVariablesFromSupergridMutation(ad: SpotifyCreateData, status: AdStatus): CreateAdInput {
	const result = {
		groupId:          ad.parent?.id,
		adAdvertiserName: ad.adAdvertiserName,
		adAssets:         {
			assetId:          ad.adAsset?.id,
			companionAssetId: ad.adCompanionAsset?.id,
			logoAssetId:      ad.adLogoAsset?.id,
			canvasAssetId:    ad.adCanvasAsset?.id,
		},
		adTagline:                     ad.adTagline,
		adName:                        ad.name,
		adHash:                        ad.hash ?? generateMutationHash(),
		adDelivery:                    'ON',
		adCallToActionLanguage:        ad.adCallToActionLanguage,
		adCallToActionClickthroughUrl: ad.adCallToActionClickthroughUrl,
		adCallToActionKey:             ad.adCallToActionKey,
		...(ad.adStartTime && {
			adStartTime: ad.adStartTime,
		}),
		...(ad.adEndTime && {
			adEndTime: ad.adEndTime,
		}),
		...(ad.adThirdPartyTrackingUrl && {
			adThirdPartyTracking: [{
				measurementPartner: ad.adThirdPartyTrackingMeasurementPartner,
				url:                ad.adThirdPartyTrackingUrl,
			}],
		}),
		adStatus: status,
	};

	return result;
}
